import { useCallback, useEffect, useRef } from "react";

// Asks before closing a form with unsaved edits (close button, backdrop, Esc, page leave).
export default function useCloseGuard(open, value, close) {
  const initial = useRef(null);
  const latest = useRef(value);
  latest.current = value;
  useEffect(() => {
    initial.current = null;
    if (!open) return;
    // Snapshot after mount-time auto-fills (e.g. coding topics from skills) have settled.
    const t = setTimeout(() => { initial.current = JSON.stringify(latest.current); }, 400);
    return () => clearTimeout(t);
  }, [open]);
  const dirty = open && initial.current !== null && initial.current !== JSON.stringify(value);
  const requestClose = useCallback(() => {
    if (!dirty || window.confirm("You have unsaved changes. Discard them?")) close();
  }, [dirty, close]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") requestClose(); };
    const onLeave = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("keydown", onKey);
    window.addEventListener("beforeunload", onLeave);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("beforeunload", onLeave); };
  }, [open, dirty, requestClose]);
  return requestClose;
}
